import { useEffect, useMemo, useState } from 'react';
import {
  FaBriefcase,
  FaChartLine,
  FaCoins,
  FaLandmark,
  FaPiggyBank,
  FaSeedling,
  FaUniversity,
  FaWallet,
} from 'react-icons/fa';
import { Account, PortfolioSummary } from '../types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAccounts } from '../store/slices/accountsSlice';
import { fetchPortfolioSummary } from '../services/apiService';
import { formatCurrency } from '../utils/formatters';
import { useNavigation } from '../hooks/useNavigation';
import { useRouteScrollContainer } from '../hooks/useRouteScrollContainer';

const PORTFOLIO_ACCOUNT_TYPES = [
  'FIXED_DEPOSIT',
  'STOCKS',
  'MUTUAL_FUND',
  'CRYPTO',
  'GOLD',
  'NPS',
  'EPF',
  'PPF',
] as const;

const ACCOUNT_LABELS: Record<Account['type'], string> = {
  SAVINGS: 'Savings',
  DEBIT_CARD: 'Debit Card',
  CREDIT_CARD: 'Credit Card',
  LOAN: 'Loan',
  STOCKS: 'Stocks & ETFs',
  FIXED_DEPOSIT: 'Fixed Deposits',
  MUTUAL_FUND: 'Mutual Funds',
  CRYPTO: 'Crypto',
  GOLD: 'Gold',
  NPS: 'NPS',
  EPF: 'EPF',
  PPF: 'PPF',
};

const ACCOUNT_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  FIXED_DEPOSIT: FaLandmark,
  STOCKS: FaChartLine,
  MUTUAL_FUND: FaSeedling,
  CRYPTO: FaCoins,
  GOLD: FaCoins,
  NPS: FaWallet,
  EPF: FaBriefcase,
  PPF: FaPiggyBank,
};

const isPortfolioAccount = (account: Account) =>
  PORTFOLIO_ACCOUNT_TYPES.includes(account.type as typeof PORTFOLIO_ACCOUNT_TYPES[number]);

type PortfolioAccountType = typeof PORTFOLIO_ACCOUNT_TYPES[number];

interface PortfolioDisplayItem {
  type: PortfolioAccountType;
  account?: Account;
}

const getReturnPercent = (summary: PortfolioSummary) => {
  if (!summary.totalInvested) {
    return 0;
  }
  return (summary.totalProfitLoss / summary.totalInvested) * 100;
};

const formatPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

const TinyPerformanceChart = ({ positive }: { positive: boolean }) => (
  <div className="h-12 w-28 overflow-hidden rounded-md">
    <svg viewBox="0 0 120 52" className="h-full w-full" aria-hidden="true">
      <defs>
        <linearGradient id={`portfolio-chart-${positive ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={positive ? '#5fd068' : '#ef4444'} stopOpacity="0.45" />
          <stop offset="100%" stopColor={positive ? '#5fd068' : '#ef4444'} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={positive ? 'M0 42 L0 35 L14 35 L14 29 L32 29 L32 24 L52 24 L52 15 L71 15 L71 36 L120 38 L120 52 L0 52 Z' : 'M0 18 L22 20 L22 26 L42 24 L42 32 L70 34 L70 40 L120 42 L120 52 L0 52 Z'}
        fill={`url(#portfolio-chart-${positive ? 'up' : 'down'})`}
      />
      <path
        d={positive ? 'M0 35 L14 35 L14 29 L32 29 L32 24 L52 24 L52 15 L71 15 L71 36 L120 38' : 'M0 18 L22 20 L22 26 L42 24 L42 32 L70 34 L70 40 L120 42'}
        fill="none"
        stroke={positive ? '#5fd068' : '#ef4444'}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

function PortfolioScreen() {
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();
  const setRouteScrollContainer = useRouteScrollContainer<HTMLDivElement>('screen:portfolio');
  const { accounts, status: accountsStatus } = useAppSelector((state) => state.accounts);
  const [summaries, setSummaries] = useState<Record<number, PortfolioSummary>>({});
  const [summaryStatus, setSummaryStatus] = useState<'idle' | 'loading' | 'succeeded' | 'failed'>('idle');

  useEffect(() => {
    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
  }, [accountsStatus, dispatch]);

  const portfolioAccounts = useMemo(
    () => accounts.filter(isPortfolioAccount).sort((left, right) => {
      const leftIndex = PORTFOLIO_ACCOUNT_TYPES.indexOf(left.type as typeof PORTFOLIO_ACCOUNT_TYPES[number]);
      const rightIndex = PORTFOLIO_ACCOUNT_TYPES.indexOf(right.type as typeof PORTFOLIO_ACCOUNT_TYPES[number]);
      return leftIndex - rightIndex || left.name.localeCompare(right.name);
    }),
    [accounts]
  );

  const portfolioItems = useMemo<PortfolioDisplayItem[]>(() => {
    const groupedAccounts = portfolioAccounts.reduce<Record<string, Account[]>>((acc, account) => {
      acc[account.type] = [...(acc[account.type] ?? []), account];
      return acc;
    }, {});

    return PORTFOLIO_ACCOUNT_TYPES.flatMap((type) => {
      const matchingAccounts = groupedAccounts[type] ?? [];
      if (matchingAccounts.length === 0) {
        return [{ type }];
      }
      return matchingAccounts.map((account) => ({ type, account }));
    });
  }, [portfolioAccounts]);

  useEffect(() => {
    if (portfolioAccounts.length === 0) {
      setSummaries({});
      return;
    }

    let isCancelled = false;
    setSummaryStatus('loading');
    Promise.all(
      portfolioAccounts.map((account) =>
        fetchPortfolioSummary(account.id)
          .then((summary) => [account.id, summary] as const)
          .catch(() => [account.id, null] as const)
      )
    ).then((results) => {
      if (isCancelled) {
        return;
      }
      setSummaries(
        results.reduce<Record<number, PortfolioSummary>>((acc, [accountId, summary]) => {
          if (summary) {
            acc[accountId] = summary;
          }
          return acc;
        }, {})
      );
      setSummaryStatus('succeeded');
    }).catch(() => {
      if (!isCancelled) {
        setSummaryStatus('failed');
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [portfolioAccounts]);

  const totalValue = Object.values(summaries).reduce((sum, summary) => sum + summary.currentValue, 0);
  const isLoading = accountsStatus === 'loading' || summaryStatus === 'loading';

  return (
    <div ref={setRouteScrollContainer} className="relative h-full overflow-y-auto bg-background text-foreground enable-scroll">
      <div className="mx-auto flex min-h-full w-full max-w-lg flex-col pb-28">
        <header className="flex flex-shrink-0 items-center justify-between gap-4 px-5 py-2.5">
          <h1 className="text-3xl font-bold tracking-normal">Portfolio</h1>
          <div className="text-right">
            <div className="text-base font-semibold">{formatCurrency(totalValue)}</div>
            <div className="text-xs font-medium text-muted-foreground">Total value</div>
          </div>
        </header>

        {isLoading && accounts.length === 0 && (
          <div className="mx-4 mt-2 rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground">
            Loading portfolio...
          </div>
        )}

        <div className="space-y-3 px-4 pt-2">
          {portfolioItems.map((item) => {
            const { account, type } = item;
            const summary = account ? summaries[account.id] : undefined;
            const Icon = ACCOUNT_ICON[type] ?? FaUniversity;
            const returnPercent = summary ? getReturnPercent(summary) : 0;
            const hasSummary = !!summary && (summary.currentValue !== 0 || summary.totalInvested !== 0 || summary.holdings.length > 0);
            const matchingTypeCount = portfolioAccounts.filter((candidate) => candidate.type === type).length;

            return (
              <button
                type="button"
                key={account ? `account-${account.id}` : `empty-${type}`}
                onClick={() => {
                  if (account) {
                    navigateTo(`/portfolio/accounts/${account.id}`, { state: { account } });
                  }
                }}
                disabled={!account}
                className={`w-full overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${account ? 'hover:bg-muted/40' : 'cursor-default opacity-75'}`}
              >
                <div className="flex items-center gap-3 px-5 py-4">
                  <Icon className="h-5 w-5 flex-shrink-0 text-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-semibold text-foreground/80">
                      {ACCOUNT_LABELS[type] ?? account?.name}
                    </div>
                    {account && matchingTypeCount > 1 && (
                      <div className="truncate text-sm text-muted-foreground">{account.name}</div>
                    )}
                  </div>
                </div>

                {hasSummary && summary && (
                  <div className="border-t border-border bg-background/35 px-5 py-4">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div>
                        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Value</div>
                        <div className="text-2xl font-semibold leading-none">{formatCurrency(summary.currentValue)}</div>
                      </div>
                      <TinyPerformanceChart positive={summary.totalProfitLoss >= 0} />
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 text-sm">
                      <div className="text-muted-foreground">Invested</div>
                      <div className="font-medium">{formatCurrency(summary.totalInvested)}</div>
                      <div className="text-muted-foreground">Returns</div>
                      <div className={`font-medium ${summary.totalProfitLoss >= 0 ? 'text-success' : 'text-error'}`}>
                        {summary.totalProfitLoss >= 0 ? '+' : ''}{formatCurrency(summary.totalProfitLoss)}
                      </div>
                      <div className="text-muted-foreground">Absolute returns</div>
                      <div>
                        <span className={`rounded-xl px-3 py-1 text-sm font-semibold ${returnPercent >= 0 ? 'bg-success/20 text-success' : 'bg-error/20 text-error'}`}>
                          {returnPercent >= 0 ? '▲ ' : '▼ '}{formatPercent(returnPercent)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PortfolioScreen;
